import React from 'react';
import { CalendarClock, ExternalLink, ListOrdered, Package, PackageCheck, UserCircle, Warehouse } from 'lucide-react';
import { GoodsReceipt } from '../../../../../types/GoodsReceipt.types';
import { Skeleton } from '../../../../../components/shared/Skeleton/Skeleton';
import { DetailPanel } from '../../../../../components/shared/DetailPanel/DetailPanel';
import {
  DetailField,
  DetailFieldList,
  DetailHeaderButton,
  DetailIdentity,
  DetailNotes,
  DetailSection
} from '../../../../../components/shared/DetailPanel/DetailBlocks';
import { GOODS_RECEIPT_STATUS_LABELS } from '../../../../../constants/purchases';
import { formatDateTime, formatQuantity } from './format';

interface GoodsReceiptDetailProps {
  receipt: GoodsReceipt | null;
  loading: boolean;
  warehouseName: string;
  receivedByName: string;
  onOpenOrder: (purchaseOrderId: string) => void;
  onClose: () => void;
}

// Campos que la ficha pinta siempre: en el esqueleto se muestran sus etiquetas.
const FIELDS: { icon: React.ElementType; label: string }[] = [
  { icon: Warehouse, label: 'ALMACÉN' },
  { icon: CalendarClock, label: 'FECHA_DE_RECEPCIÓN' },
  { icon: UserCircle, label: 'RECIBIDO_POR' },
  { icon: Package, label: 'BULTOS' },
  { icon: PackageCheck, label: 'UNIDADES_RECIBIDAS' }
];

// Ficha de un albarán (pestaña Recepciones). goods_receipts_get no devuelve
// nombres de almacén ni de receptor: los resuelve el contenedor con los
// catálogos que ya tiene cargados y los pasa como texto.
export const GoodsReceiptDetail = ({ receipt, loading, warehouseName, receivedByName, onOpenOrder, onClose }: GoodsReceiptDetailProps) => {
  if (loading || !receipt) {
    return (
      <DetailPanel
        drawer
        title="Ficha_De_Albarán"
        onClose={onClose}
        headerActions={<DetailHeaderButton icon={ExternalLink} label="Ver orden" onClick={() => undefined} disabled />}
      >
        <DetailIdentity
          icon={PackageCheck}
          reference={<Skeleton width="100px" height="8px" />}
          name={<Skeleton width="55%" height="18px" />}
        />
        <DetailFieldList>
          {FIELDS.map(f => (
            <DetailField key={f.label} icon={f.icon} label={f.label}>
              <Skeleton width="40%" height="10px" />
            </DetailField>
          ))}
        </DetailFieldList>
        <Skeleton width="100%" height="4em" />
      </DetailPanel>
    );
  }

  const totalUnits = receipt.lines.reduce((acc, l) => acc + l.quantityReceived, 0);

  return (
    <DetailPanel
      drawer
      title="Ficha_De_Albarán"
      onClose={onClose}
      headerActions={
        <DetailHeaderButton icon={ExternalLink} label="Ver orden" onClick={() => onOpenOrder(receipt.purchaseOrderId)} />
      }
    >
      <DetailIdentity
        icon={PackageCheck}
        reference={`${receipt.poNumber ? `${receipt.poNumber} · ` : ''}ALBARÁN #${receipt.id}`}
        name={receipt.deliveryNoteNumber || 'Sin número de albarán'}
        subtitle={
          <span className={`module-badge ${receipt.status === 'complete' ? 'completed' : 'pending'}`}>
            {GOODS_RECEIPT_STATUS_LABELS[receipt.status]}
          </span>
        }
      />

      <DetailFieldList>
        <DetailField icon={Warehouse} label="ALMACÉN">{warehouseName || `#${receipt.warehouseId}`}</DetailField>
        <DetailField icon={CalendarClock} label="FECHA_DE_RECEPCIÓN">{formatDateTime(receipt.receivedAt)}</DetailField>
        <DetailField icon={UserCircle} label="RECIBIDO_POR">
          {receivedByName || (receipt.receivedBy ? `Usuario #${receipt.receivedBy}` : '—')}
        </DetailField>
        <DetailField icon={Package} label="BULTOS">{receipt.packageCount === null ? 'No declarados' : receipt.packageCount}</DetailField>
        <DetailField icon={PackageCheck} label="UNIDADES_RECIBIDAS">{formatQuantity(totalUnits)}</DetailField>
      </DetailFieldList>

      <DetailSection icon={ListOrdered} title="Líneas">
        <div className="purchases-view__lines-wrapper">
          <table className="purchases-view__lines-table purchases-view__lines-table--readonly">
            <thead>
              <tr>
                <th>Artículo</th>
                <th className="right">En este albarán</th>
                <th className="right">Pedida</th>
                <th className="right">Acumulada</th>
              </tr>
            </thead>
            <tbody>
              {receipt.lines.map(l => (
                <tr key={l.id}>
                  <td>
                    <div className="purchases-view__line-desc">{l.description}</div>
                    {l.sku && <div className="purchases-view__line-sub">{l.sku}</div>}
                  </td>
                  <td className="right purchases-view__line-total">{formatQuantity(l.quantityReceived)}</td>
                  <td className="right">{formatQuantity(l.quantityOrdered)}</td>
                  <td className="right">{formatQuantity(l.lineQuantityReceived)}</td>
                </tr>
              ))}
              {receipt.lines.length === 0 && (
                <tr><td colSpan={4} className="purchases-view__lines-empty">Albarán sin líneas registradas</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </DetailSection>

      {receipt.notes && <DetailNotes title="Notas de la recepción">{receipt.notes}</DetailNotes>}
    </DetailPanel>
  );
};
